import { useRef, useState, memo, useCallback, useEffect, useMemo } from "react";
import { validateVehicleImage } from "../../services/geminiService";
import { useAgentContext } from "../../contexts/AgentContext";
import ImageUpload from "../image-upload/ImageUpload";
import { vectorFAQService } from "../../services/vectorFAQService";
import {
  AltairComponentProps,
  ClaimPolicyData,
  CompanyWithPackages,
  ContractedWorkshop,
  CustomerNationalIdData,
  NonContractedWorkshopFormData,
  PolicyPackage,
  VehicleSide,
  VehicleSurveyImage,
} from "../../interfaces/altair";
import { FlowStep } from "../../agents/types";
import { useAltairChartEffect } from "./altairChartEffect";
import { createHandleSelectPackage } from "./handleSelectPackage";
import { createHandleImagesUploaded } from "./handleImagesUploaded";
import { compressBase64Image } from "../../utils/imageCompression";
import {
  policyInquiry,
  chooseContractedWorkshop,
  chooseNonContractedWorkshop,
} from "../../services/claimService";
import {
  ExtractedDataConfirmationModal,
  ExtractedDataField,
} from "../extracted-data-confirmation";
import { PolicyNumberModal } from "../policy-number-modal";
import {
  systemUiMessage,
  USER_CLICKED_PACKAGE_CONFIRM,
  USER_CLICKED_POLICY_CONFIRM,
  USER_SUBMITTED_POLICY_INQUIRY,
  USER_SELECTED_LIVE_SURVEY,
  USER_SELECTED_UPLOAD_IMAGES,
  USER_UPLOADED_VEHICLE_IMAGES,
  USER_SELECTED_CONTRACTED_WORKSHOP,
  USER_SELECTED_NON_CONTRACTED_WORKSHOP,
  USER_CHOSE_CONTRACTED_WORKSHOP,
  USER_SUBMITTED_NON_CONTRACTED_WORKSHOP,
  USER_ACKNOWLEDGED_CLAIM_NUMBER,
  USER_CAPTURED_VEHICLE_SIDE,
} from "../../constants/ameenSignals";
import { PackageSelection } from "../package-selection/PackageSelection";
import { DocumentUploadSection } from "../document-upload-section/DocumentUploadSection";
import { FullScreenCameraCapture } from "../full-screen-camera-capture/FullScreenCameraCapture";
import { VehicleSurveyOverlay } from "../vehicle-survey-overlay/VehicleSurveyOverlay";
import { SurveyOptions } from "../survey-options/SurveyOptions";
import { PolicyVehicleUpload } from "../policy-vehicle-upload/PolicyVehicleUpload";
import { ClaimVehicleUpload } from "../claim-vehicle-upload/ClaimVehicleUpload";
import { PolicyConfirmation } from "../policy-confirmation/PolicyConfirmation";
import { ClaimPolicyInquiry } from "../claim-policy-inquiry/ClaimPolicyInquiry";
import { ClaimWorkshopOptions } from "../claim-workshop-options/ClaimWorkshopOptions";
import { ContractedWorkshopsList } from "../contracted-workshops-list/ContractedWorkshopsList";
import { NonContractedWorkshopForm } from "../non-contracted-workshop-form/NonContractedWorkshopForm";
import { ClaimNumberDisplay } from "../claim-number-display/ClaimNumberDisplay";
import {
  UnderwritingChoicePopup,
  UnderwritingNumberInputPopup,
} from "../underwriting-questions";
import { createHandleUnderwritingAnswer } from "./handleUnderwritingAnswer";
import {
  fetchUnderwritingQuestions,
  UnderwritingQuestion,
} from "../../services/lookupService";
import { muteMicForPhase, unmuteMicForPhase } from "../../utils/micMuteRegistry";

import "./Altair.scss";


// Extended props to include camera control callbacks from parent
export interface AltairWithCameraProps extends AltairComponentProps {
  onRequestStartCamera?: () => Promise<boolean>;
  onRequestCaptureFrame?: () => Promise<string | null>;
  onRequestStopCamera?: () => void;
}

function AltairComponent({
  permissions,
  userData,
  voiceGender = "male",
  onRequestStartCamera,
  onRequestCaptureFrame,
  onRequestStopCamera,
}: AltairWithCameraProps) {
  // Get agent context - this replaces useLiveAPIContext
  // IMPORTANT: Tool handling is now done in AgentNodes, not here!
  const {
    connected,
    connect,
    send,
    client,
    flowStep,
    setFlowStep,
    companies,
    setCompanies,
    contractedWorkshops: agentContractedWorkshops,
    setContractedWorkshops: setAgentContractedWorkshops,
    setUIStateManager,
    uiStateManager,
    setLoading,
    currentAgentId,
  } = useAgentContext();

  // Stable ref so handleSurveyCaptureClick can call agent-registered callbacks
  // without needing uiStateManager in its dependency array (avoids excessive re-renders).
  const uiStateManagerRef = useRef(uiStateManager);
  useEffect(() => {
    uiStateManagerRef.current = uiStateManager;
  }, [uiStateManager]);

  /**
   * Stable per-side damage description store for the button-capture path.
   * Written here (useRef → never triggers re-renders) so it survives UIStateManager
   * re-creations that happen every time surveyImages/claimSurveyImages state changes.
   * The agent reads this via getButtonPathDamages() at return_to_caller time.
   */
  const buttonPathDamagesRef = useRef<Record<string, string | null>>({});

  // Altair chart state
  const [jsonString, setJSONString] = useState<string>("");

  // Local state for package selection (UI only)
  const [selectedPackageCode, setSelectedPackageCode] = useState<string>("");

  const [customerNationalIdData, setCustomerNationalIdData] =
    useState<CustomerNationalIdData>({
      national_id_number: "",
      profession: "",
      gender: "",
      religion: "",
      marital_status: "",
      expiration_date: "",
      issue_batch_date: "",
      full_name: "",
      address: "",
      birth_date: "",
      card_serial_number: "",
      national_id_front_image_base64: "",
      national_id_back_image_base64: "",
    });

  // Vehicle Survey state
  const [surveyImages, setSurveyImages] = useState<VehicleSurveyImage[]>([]);

  // Claim Request state
  const [claimPolicyData, setClaimPolicyData] =
    useState<ClaimPolicyData | null>(null);
  // Use contractedWorkshops from agent context
  const contractedWorkshops = agentContractedWorkshops;
  const setContractedWorkshops = setAgentContractedWorkshops;
  const [claimSurveyImages, setClaimSurveyImages] = useState<
    VehicleSurveyImage[]
  >([]);
  const [policyNumberInput, setPolicyNumberInput] = useState<string>("");
  const [nonContractedForm, setNonContractedForm] =
    useState<NonContractedWorkshopFormData>({
      city: "",
      area: "",
      workshopAddress: "",
      workshopName: "",
      contactPersonName: "",
      mobile: "",
      phoneNumber: "",
      hotline: "",
    });

  const [hasGreeted, setHasGreeted] = useState<boolean>(false);
  const [journeyTitle, setJourneyTitle] = useState<"policy" | "claim" | null>(
    null,
  );

  // ── Document OCR Phase tracking ──────────────────────────────
  // Keeps camera overlay alive across all document steps (national_id_front, back, etc.)
  // even during intermediate "idle" gaps (confirmation popup, backend processing).
  const DOCUMENT_FLOW_STEPS = new Set([
    "national_id_front",
    "national_id_back",
    "driving_license",
    "vehicle_license_front",
    "vehicle_license_back",
  ]);
  const [documentPhaseActive, setDocumentPhaseActive] = useState(false);
  const [shouldMuteMic, setShouldMuteMic] = useState(false);
  const lastDocTitleRef = useRef("رفع مستند");

  const flowStepRef = useRef(flowStep);
  useEffect(() => {
    flowStepRef.current = flowStep;
  }, [flowStep]);

  useEffect(() => {
    if (DOCUMENT_FLOW_STEPS.has(flowStep)) {
      setDocumentPhaseActive(true);
      
      // Unmute at vehicle license steps
      if (flowStep === "vehicle_license_front" || flowStep === "vehicle_license_back") {
        setShouldMuteMic(false);
      } else {
        setShouldMuteMic(true);
      }

      // Update title to match current document step
      const titles: Record<string, string> = {
        national_id_front: "رفع صورة وش بطاقة الرقم القومي",
        national_id_back: "رفع صورة ظهر بطاقة الرقم القومي",
        driving_license: "رفع صورة رخصة القيادة",
        vehicle_license_front: "رفع صورة وش رخصة السيارة",
        vehicle_license_back: "رفع صورة ظهر رخصة السيارة",
      };
      lastDocTitleRef.current = titles[flowStep] || "رفع مستند";
    } else if (flowStep !== "idle") {
      // Moved to a genuinely different phase (underwriting, package, survey, etc.)
      setDocumentPhaseActive(false);
      setShouldMuteMic(false);
    }
    // When flowStep === "idle", we intentionally keep state unchanged
    // so the camera stays open and mic stays in its previous state during confirmation popup / backend processing gaps
  }, [flowStep]);

  // ── Mute mic during document upload phase ────────────────────
  // The user is taking photos / uploading documents — their voice would interfere
  // with the AI. We mute the mic for the initial phases, but unmute for vehicle license.
  useEffect(() => {
    if (shouldMuteMic && connected) {
      muteMicForPhase();
    } else {
      unmuteMicForPhase();
    }
    return () => {
      // Cleanup: ensure mic is unmuted if the component unmounts
      unmuteMicForPhase();
    };
  }, [shouldMuteMic, connected]);

  // ── Reset all local UI state when session ends (disconnect or error) ─────
  useEffect(() => {
    if (!connected) {
      // Close camera overlay
      setDocumentPhaseActive(false);
      lastDocTitleRef.current = "رفع مستند";
      // Dismiss any open modals
      setExtractedDataModal({
        isOpen: false,
        title: "",
        data: [],
        documentType: "",
        previewImageSrc: "",
        onConfirm: () => { },
      });
      setPolicyNumberModal((prev) => ({ ...prev, isOpen: false }));
      // Clear selected package
      setSelectedPackageCode("");
      setJourneyTitle(null);
    }
  }, [connected]);

  // Show journey title when user enters policy/claim flow.
  useEffect(() => {
    if (currentAgentId === "policy_agent") {
      setJourneyTitle("policy");
    } else if (currentAgentId === "claim_agent") {
      setJourneyTitle("claim");
    }
  }, [currentAgentId]);

  // Keep title visible until the target UI step is reached.
  useEffect(() => {
    if (flowStep === "package_selection" && journeyTitle === "policy") {
      setJourneyTitle(null);
    }
    if (flowStep === "claim_survey_options" && journeyTitle === "claim") {
      setJourneyTitle(null);
    }
  }, [flowStep, journeyTitle]);



  // Underwriting questions UI config (fetched from .NET Lookup API once, early)
  const [underwritingQuestions, setUnderwritingQuestions] = useState<
    UnderwritingQuestion[] | null
  >(null);
  const underwritingFetchedRef = useRef(false);

  // Fetch underwriting questions once, early in the session,
  // so they are ready before the underwriting agent starts asking.
  useEffect(() => {
    if (underwritingFetchedRef.current) return;
    underwritingFetchedRef.current = true;
    fetchUnderwritingQuestions()
      .then((qs) => setUnderwritingQuestions(qs))
      .catch((err) => {
        console.error("[Altair] Failed to fetch underwriting questions:", err);
        setUnderwritingQuestions([]);
      });
  }, []);

  const getUnderwritingQuestionByFlowStep = useCallback(
    (step: string) => underwritingQuestions?.find((q) => q.flowStep === step),
    [underwritingQuestions],
  );

  const coerceUnderwritingValue = useCallback((raw: string) => {
    const v = String(raw);
    if (v === "true") return true;
    if (v === "false") return false;
    if (/^-?\d+(\.\d+)?$/.test(v)) return Number(v);
    return v;
  }, []);

  // Modal states
  const [extractedDataModal, setExtractedDataModal] = useState<{
    isOpen: boolean;
    title: string;
    data: ExtractedDataField[];
    documentType: string;
    previewImageSrc?: string;
    onConfirm: (editedData?: ExtractedDataField[]) => void;
  }>({
    isOpen: false,
    title: "",
    data: [],
    documentType: "",
    previewImageSrc: "",
    onConfirm: () => { },
  });

  const [policyNumberModal, setPolicyNumberModal] = useState<{
    isOpen: boolean;
    policyNumber: string;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    policyNumber: "",
    title: "رقم الوثيقة",
    message: "تم إصدار الوثيقة بنجاح. يرجى حفظ رقم الوثيقة:",
    onConfirm: () => { },
  });

  const TEST_MODE = false;

  // Client is now from useAgentContext (defined above)
  const embedRef = useRef<HTMLDivElement>(null);

  // NOTE: Connection is triggered by user clicking the microphone button in ControlTray
  // DO NOT auto-connect here - user must initiate the session

  // ═══════════════════════════════════════════════════════════
  // VEHICLE SURVEY CAMERA CALLBACKS
  // ═══════════════════════════════════════════════════════════

  const handleStartCameraStream = useCallback(async (): Promise<boolean> => {
    console.log("📹 [Altair] Starting camera stream for survey...");
    if (onRequestStartCamera) {
      const success = await onRequestStartCamera();
      if (success) {
        // Clear per-side damage store so a fresh survey starts clean
        buttonPathDamagesRef.current = {};

        // Clear any previous survey images when starting a new survey
        // Determine if we're in ClaimFlow or PolicyFlow
        // Note: flowStep might not be updated yet when this is called, so we check both
        const isClaimFlow =
          flowStep === "claim_vehicle_survey" ||
          flowStep?.startsWith("claim_") ||
          false;
        if (isClaimFlow) {
          setClaimSurveyImages([]);
          // Also clear from session storage
          try {
            const existingData = sessionStorage.getItem("saaed_user_data");
            const userData = existingData ? JSON.parse(existingData) : {};
            userData.claimSurveyImages = [];
            sessionStorage.setItem("saaed_user_data", JSON.stringify(userData));
          } catch (error) {
            console.error(
              "❌ [Altair] Failed to clear claim survey images from session storage:",
              error,
            );
          }
        } else {
          setSurveyImages([]);
          // Also clear from session storage
          try {
            const existingData = sessionStorage.getItem("saaed_user_data");
            const userData = existingData ? JSON.parse(existingData) : {};
            userData.surveyImages = [];
            sessionStorage.setItem("saaed_user_data", JSON.stringify(userData));
          } catch (error) {
            console.error(
              "❌ [Altair] Failed to clear policy survey images from session storage:",
              error,
            );
          }
        }
      }
      return success;
    }
    console.warn("⚠️ [Altair] onRequestStartCamera not provided");
    return false;
  }, [onRequestStartCamera, flowStep]);

  const underwritingHandlers = useMemo(
    () =>
      createHandleUnderwritingAnswer(
        send,
        setFlowStep,
        (msg, type) => setLoading(true, msg, type),
        () => setLoading(false),
      ),
    [send, setFlowStep, setLoading],
  );

  const handleTakeVehiclePicture = useCallback(
    async (
      side: string,
      currentFlowStep?: FlowStep,
    ): Promise<string | null> => {
      console.log(`📸 [Altair] Taking picture for side: ${side}`);
      if (onRequestCaptureFrame) {
        const imageData = await onRequestCaptureFrame();
        if (imageData) {
          // Compress the image automatically
          let compressedImageData = imageData;
          try {
            console.log(`🔄 [Altair] Compressing image for ${side}...`);
            compressedImageData = await compressBase64Image(imageData, {
              maxSizeMB: 2,
              maxWidthOrHeight: 1920,
              quality: 0.85,
            });
            console.log(`✅ [Altair] Image compressed for ${side}`);
          } catch (error) {
            console.error(
              `❌ [Altair] Error compressing image for ${side}, using original:`,
              error,
            );
          }

          const newImage: VehicleSurveyImage = {
            side: side as VehicleSide,
            data: compressedImageData,
            capturedAt: new Date().toISOString(),
          };

          // Determine if we're in ClaimFlow or PolicyFlow based on flowStep
          // Check both the passed flowStep parameter and the current flowStep state
          // Also check if we already have claim survey images as a fallback
          const isClaimFlow =
            (currentFlowStep || flowStep)?.startsWith("claim_") ||
            flowStep === "claim_vehicle_survey" ||
            (claimSurveyImages.length > 0 && surveyImages.length === 0);

          if (isClaimFlow) {
            // Save to claim survey images for ClaimFlow
            setClaimSurveyImages((prev) => {
              const filtered = prev.filter((img) => img.side !== side);
              const updated = [...filtered, newImage];

              // Also save to session storage
              try {
                const existingData = sessionStorage.getItem("saaed_user_data");
                const userData = existingData ? JSON.parse(existingData) : {};
                const existingImages = userData.claimSurveyImages || [];
                const filteredImages = existingImages.filter(
                  (img: VehicleSurveyImage) => img.side !== side,
                );
                userData.claimSurveyImages = [...filteredImages, newImage];
                sessionStorage.setItem(
                  "saaed_user_data",
                  JSON.stringify(userData),
                );
                console.log(
                  `✅ [Altair] Claim survey image for ${side} saved to state and session storage`,
                );
              } catch (error) {
                console.error(
                  "❌ [Altair] Failed to save claim survey image to session storage:",
                  error,
                );
              }

              return updated;
            });
          } else {
            // Save to policy survey images for PolicyFlow
            setSurveyImages((prev) => {
              const filtered = prev.filter((img) => img.side !== side);
              const updated = [...filtered, newImage];

              // Also save to session storage
              try {
                const existingData = sessionStorage.getItem("saaed_user_data");
                const userData = existingData ? JSON.parse(existingData) : {};
                const existingImages = userData.surveyImages || [];
                const filteredImages = existingImages.filter(
                  (img: VehicleSurveyImage) => img.side !== side,
                );
                userData.surveyImages = [...filteredImages, newImage];
                sessionStorage.setItem(
                  "saaed_user_data",
                  JSON.stringify(userData),
                );
                console.log(
                  `✅ [Altair] Policy survey image for ${side} saved to state and session storage`,
                );
              } catch (error) {
                console.error(
                  "❌ [Altair] Failed to save policy survey image to session storage:",
                  error,
                );
              }

              return updated;
            });
          }

          return compressedImageData;
        }
      }
      console.warn("⚠️ [Altair] onRequestCaptureFrame not provided or failed");
      return null;
    },
    [onRequestCaptureFrame, flowStep, claimSurveyImages, surveyImages],
  );

  const handleCloseCameraStream = useCallback(() => {
    console.log("📹 [Altair] Closing camera stream...");
    if (onRequestStopCamera) {
      onRequestStopCamera();
    }
  }, [onRequestStopCamera]);

  // Order for manual capture button (same as take_vehicle_picture flow)
  const SURVEY_SIDE_ORDER: VehicleSide[] = [
    "Chassis",
    "Front",
    "Right",
    "Back",
    "Left",
  ];

  const nextSideToCapture = useMemo((): VehicleSide | null => {
    const isClaim =
      flowStep === "claim_vehicle_survey" ||
      (claimSurveyImages.length > 0 && surveyImages.length === 0);
    const current = isClaim ? claimSurveyImages : surveyImages;
    const captured = new Set(current.map((img) => img.side));
    for (const side of SURVEY_SIDE_ORDER) {
      if (!captured.has(side)) return side;
    }
    return null;
  }, [flowStep, surveyImages, claimSurveyImages]);

  const [surveyCaptureInProgress, setSurveyCaptureInProgress] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Removes a survey image by side from state AND mirrors the removal to sessionStorage.
  // Called on validation failure (rollback contract: image stored optimistically, removed if invalid).
  const removeSurveyImage = useCallback((side: string) => {
    const isClaimFlow =
      flowStep?.startsWith("claim_") ||
      flowStep === "claim_vehicle_survey" ||
      (claimSurveyImages.length > 0 && surveyImages.length === 0);

    if (isClaimFlow) {
      setClaimSurveyImages((prev) => {
        const updated = prev.filter((img) => img.side !== side);
        try {
          const existingData = sessionStorage.getItem("saaed_user_data");
          const userData = existingData ? JSON.parse(existingData) : {};
          userData.claimSurveyImages = updated;
          sessionStorage.setItem("saaed_user_data", JSON.stringify(userData));
        } catch {
          // ignore storage errors
        }
        return updated;
      });
    } else {
      setSurveyImages((prev) => {
        const updated = prev.filter((img) => img.side !== side);
        try {
          const existingData = sessionStorage.getItem("saaed_user_data");
          const userData = existingData ? JSON.parse(existingData) : {};
          userData.surveyImages = updated;
          sessionStorage.setItem("saaed_user_data", JSON.stringify(userData));
        } catch {
          // ignore storage errors
        }
        return updated;
      });
    }
  }, [flowStep, claimSurveyImages.length, surveyImages.length]);

  const handleSurveyCaptureClick = useCallback(async () => {
    if (nextSideToCapture == null || !onRequestCaptureFrame || isValidating) return;
    const sideCaptured = nextSideToCapture;
    setIsValidating(true);
    setValidationError(null);
    setSurveyCaptureInProgress(true);
    try {
      // 1. Capture + store image optimistically (handleTakeVehiclePicture unchanged)
      const captured = await handleTakeVehiclePicture(sideCaptured, flowStep);
      if (!captured) return;

      // 2. Get vehicleMake/model from agent via UIStateManager
      //    Agent registered getVehicleMakeModel reading from context.transitionData.flow_state
      const { make: vehicleMake, model: vehicleModel } =
        uiStateManagerRef.current?.getVehicleMakeModel?.() ?? {};

      // 3. Validate the captured image
      const result = await validateVehicleImage(captured, sideCaptured, vehicleMake, vehicleModel);

      // 4a. Invalid image — rollback and show error
      if (!result.isValid) {
        removeSurveyImage(sideCaptured);
        setValidationError(result.message || "الصورة مش صحيحة. حاول تاني.");
        return;
      }

      // 4b. Chassis make/model mismatch — rollback and show error
      if (sideCaptured === "Chassis" && result.makeModelMatchResult === "mismatch") {
        removeSurveyImage(sideCaptured);
        setValidationError(result.message || "رقم الشاسيه مش مطابق. صور تاني.");
        return;
      }

      // 4c. Valid — update agent closure state FIRST, then signal Gemini Live
      setValidationError(null);

      // Write to the stable ref FIRST — this survives UIStateManager re-creations
      // caused by surveyImages/claimSurveyImages state changes (which happen right
      // after this due to the optimistic image store in handleTakeVehiclePicture).
      buttonPathDamagesRef.current[sideCaptured] = result.damageDescription ?? null;

      // Also attempt the agent closure update (best-effort; may be a stale ref
      // if UIStateManager was recreated since the last tool call).
      uiStateManagerRef.current?.onButtonCaptureValidated?.({
        side: sideCaptured,
        damageDescription: result.damageDescription,
        confidenceScore: result.confidenceScore,
        chassisNumber: result.chassisNumber,
        makeModelMatchResult: result.makeModelMatchResult,
      });

      // Signal Gemini Live after agent state is updated.
      // Include the damage description so the model knows what the API found
      // for this side and can relay it to the user (or accumulate it).
      const damagePayload = result.damageDescription
        ? `${sideCaptured} damage_found=${result.damageDescription}`
        : `${sideCaptured} no_damage`;
      send(
        { text: systemUiMessage(USER_CAPTURED_VEHICLE_SIDE, damagePayload) },
        true
      );
    } catch {
      // Network/unexpected error — rollback image and show error
      removeSurveyImage(sideCaptured);
      setValidationError("حصل خطأ في التحقق من الصورة. حاول تاني.");
    } finally {
      setIsValidating(false);
      setSurveyCaptureInProgress(false);
    }
  }, [
    nextSideToCapture,
    flowStep,
    handleTakeVehiclePicture,
    onRequestCaptureFrame,
    removeSurveyImage,
    isValidating,
    send,
  ]);

  const getSurveyImages = useCallback((): VehicleSurveyImage[] => {
    return surveyImages;
  }, [surveyImages]);

  // ═══════════════════════════════════════════════════════════
  // CLAIM REQUEST CALLBACKS
  // ═══════════════════════════════════════════════════════════

  const getClaimSurveyImages = useCallback((): VehicleSurveyImage[] => {
    console.log(
      "📸 [Altair] getClaimSurveyImages called, current claimSurveyImages count:",
      claimSurveyImages.length,
    );

    // Try to get from state first
    if (claimSurveyImages && claimSurveyImages.length > 0) {
      console.log(
        "✅ [Altair] Returning claim survey images from state:",
        claimSurveyImages.length,
      );
      return claimSurveyImages;
    }

    // Fallback: try to get from session storage
    try {
      const existingData = sessionStorage.getItem("saaed_user_data");
      if (existingData) {
        const userData = JSON.parse(existingData);
        if (
          userData.claimSurveyImages &&
          Array.isArray(userData.claimSurveyImages) &&
          userData.claimSurveyImages.length > 0
        ) {
          console.log(
            "✅ [Altair] Returning claim survey images from session storage:",
            userData.claimSurveyImages.length,
          );
          return userData.claimSurveyImages;
        }
      }
    } catch (error) {
      console.error(
        "❌ [Altair] Error reading claim survey images from session storage:",
        error,
      );
    }

    console.warn(
      "⚠️ [Altair] No claim survey images found in state or session storage",
    );
    return [];
  }, [claimSurveyImages]);

  // Initialize vector FAQ service when component mounts
  useEffect(() => {
    vectorFAQService.initialize().catch((error) => {
      console.error(
        "❌ [Amin] Failed to initialize vector FAQ service:",
        error,
      );
    });
  }, []);

  // NOTE: useGreetingEffect, useModelConfigEffect, and useToolCallEffect have been REMOVED
  // Tool handling is now done in AgentNodes (single source of truth)
  // Greeting is triggered by the initial agent (greeting_agent)
  // Model config comes from backend via ephemeral tokens

  // Callback to show policy number modal
  const handleShowPolicyNumber = useCallback(
    (
      policyNumber: string,
      title: string,
      message: string,
      onConfirm: () => void,
    ) => {
      setPolicyNumberModal({
        isOpen: true,
        policyNumber,
        title,
        message,
        onConfirm: () => {
          setPolicyNumberModal((prev) => ({ ...prev, isOpen: false }));
          onConfirm();
        },
      });
    },
    [],
  );

  // Register UI State Manager with AgentContext so AgentNodes can update UI
  useEffect(() => {
    if (setUIStateManager) {
      setUIStateManager({
        setFlowStep,
        getFlowStep: () => flowStep,
        setCompanies,
        getCompanies: () => companies,
        setContractedWorkshops,
        getContractedWorkshops: () => contractedWorkshops,
        getSurveyImages,
        setSurveyImages,
        getClaimSurveyImages,
        setClaimSurveyImages,
        removeSurveyImage,
        getCustomerNationalIdData: () => customerNationalIdData,
        setCustomerNationalIdData,
        getClaimPolicyData: () => claimPolicyData,
        setClaimPolicyData,
        showPolicyNumber: handleShowPolicyNumber,
        showExtractedDataConfirmation: handleShowExtractedDataConfirmation,
        showLoading: (
          message: string,
          type?: import("../../agents/types").LoadingType,
        ) => setLoading(true, message, type),
        hideLoading: () => setLoading(false),
        onStartCameraStream: handleStartCameraStream,
        onTakeVehiclePicture: handleTakeVehiclePicture,
        onCloseCameraStream: handleCloseCameraStream,
        // Stable ref accessors — survive UIStateManager re-creations between captures
        getButtonPathDamages: () => ({ ...buttonPathDamagesRef.current }),
        clearButtonPathDamages: () => { buttonPathDamagesRef.current = {}; },
        saveToSessionStorage: (key: string, data: any) => {
          try {
            sessionStorage.setItem(key, JSON.stringify(data));
          } catch (e) {
            console.error("Failed to save to session storage:", e);
          }
        },
        getFromSessionStorage: (key: string) => {
          try {
            return JSON.parse(sessionStorage.getItem(key) || "null");
          } catch (e) {
            return null;
          }
        },
        getClient: () => client,
      });
    }
  }, [
    setUIStateManager,
    setFlowStep,
    flowStep,
    setCompanies,
    companies,
    setContractedWorkshops,
    contractedWorkshops,
    getSurveyImages,
    setSurveyImages,
    getClaimSurveyImages,
    setClaimSurveyImages,
    removeSurveyImage,
    customerNationalIdData,
    setCustomerNationalIdData,
    claimPolicyData,
    setClaimPolicyData,
    handleShowPolicyNumber,
    setLoading,
    handleStartCameraStream,
    handleTakeVehiclePicture,
    handleCloseCameraStream,
    client,
  ]);

  useAltairChartEffect({ embedRef, jsonString });

  // Callback to show extracted data confirmation modal
  const handleShowExtractedDataConfirmation = useCallback(
    (
      data: ExtractedDataField[],
      title: string,
      documentType: string,
      onConfirm: (editedData?: ExtractedDataField[]) => void,
      previewImageSrc?: string,
    ) => {
      setExtractedDataModal({
        isOpen: true,
        title,
        data,
        documentType,
        previewImageSrc,
        onConfirm: (editedData?: ExtractedDataField[]) => {
          setExtractedDataModal((prev) => ({ ...prev, isOpen: false }));
          onConfirm(editedData);
        },
      });
    },
    [],
  );

  const handleSelectPackage = createHandleSelectPackage({
    setSelectedPackageCode,
    setFlowStep,
    client,
  });

  const handlePackageConfirm = useCallback(
    (selectedPkg: PolicyPackage) => {
      if (!send) {
        console.warn(
          "⚠️ [Altair] Send not available for package confirm click",
        );
        return;
      }

      console.log("✅ [Ameen] User confirmed package:", selectedPkg);

      // Save package price to session storage
      try {
        sessionStorage.setItem(
          "selected_package_price",
          selectedPkg.totalpremium.toString(),
        );
        console.log(
          `✅ [Altair] Saved selected package price ${selectedPkg.totalpremium} to session storage`,
        );
      } catch (error) {
        console.error(
          "❌ [Altair] Failed to save package price to session storage:",
          error,
        );
      }

      // Send confirm event via context (manager) so it reaches the active session
      const payload = `${selectedPkg.packageCode}|${selectedPkg.packageTypeAr}|${selectedPkg.packageTypeEn}|${selectedPkg.totalpremium}`;
      send(
        { text: systemUiMessage(USER_CLICKED_PACKAGE_CONFIRM, payload) },
        true,
      );

      setFlowStep("idle");
    },
    [send, setFlowStep],
  );

  const handleImagesUploaded = createHandleImagesUploaded({
    send,
    getFlowStep: () => flowStepRef.current,
    setCustomerNationalIdData,
    setFlowStep,
    onShowExtractedDataConfirmation: handleShowExtractedDataConfirmation,
    showLoading: (
      message?: string,
      type?: import("../../agents/types").LoadingType,
    ) =>
      setLoading(
        true,
        message ?? "جاري رفع المستند ومعالجة البيانات...",
        type ?? "document_upload",
      ),
    hideLoading: () => setLoading(false),
  });

  const selectedPackage = (() => {
    for (const company of companies) {
      const pkg = company.companyPackages.find(
        (pkg: PolicyPackage) => pkg.packageCode === selectedPackageCode,
      );
      if (pkg) return pkg;
    }
    return null;
  })();

  // Save the package price to sessionStorage when confirmation UI is displayed
  // This ensures the price shown matches what will be sent to GenerateFawryPayLink
  useEffect(() => {
    if (flowStep === "policy_confirmation" && selectedPackage) {
      try {
        sessionStorage.setItem(
          "selected_package_price",
          selectedPackage.totalpremium.toString(),
        );
        console.log(
          `✅ [Altair] Saved policy confirmation price ${selectedPackage.totalpremium} to session storage (matches confirmation UI display)`,
        );
      } catch (error) {
        console.error(
          "❌ [Altair] Failed to save policy confirmation price to session storage:",
          error,
        );
      }
    }
  }, [flowStep, selectedPackage]);

  const handleConfirmPolicyClick = useCallback(() => {
    if (!send) {
      console.warn("⚠️ [Altair] Send not available for confirm click");
      return;
    }

    console.log("✅ [Ameen] User clicked policy confirmation button");

    // Hide confirmation view immediately
    setFlowStep("idle");

    // Notify Ameen via system_ui so he can call confirm_policy_request tool (send goes through manager's active client)
    send({ text: systemUiMessage(USER_CLICKED_POLICY_CONFIRM) }, false);

    // Send next instruction
    setTimeout(() => {
      // Intentionally left blank or can be removed if not needed.
      // Replaced by backend instruction handling.
    }, 300);
  }, [send, setFlowStep]);

  // ═══════════════════════════════════════════════════════════
  // CLAIM REQUEST EVENT HANDLERS
  // ═══════════════════════════════════════════════════════════

  const handlePolicyInquirySubmit = useCallback(async () => {
    if (!send || !policyNumberInput.trim()) {
      console.warn("⚠️ [Altair] Send not available or policy number empty");
      return;
    }

    console.log("📋 [Altair] Submitting policy inquiry:", policyNumberInput);

    try {
      setLoading(true, "جاري التحقق من الوثيقة...", "api");
      const result = await policyInquiry(policyNumberInput);
      setLoading(false);
      console.log("✅ [Altair] Policy inquiry result:", result);

      if (result.found) {
        setClaimPolicyData(result);
        // Save to session storage
        try {
          sessionStorage.setItem(
            "saaed_claim_data",
            JSON.stringify({
              policyNumber: result.policyNumber,
              claimRequestNum: result.claimRequestNum,
            }),
          );
        } catch (e) {
          console.error("❌ [Altair] Failed to save claim data:", e);
        }

        setFlowStep("idle");
        setPolicyNumberInput("");

        // Notify Ameen
        send(
          {
            text: systemUiMessage(
              USER_SUBMITTED_POLICY_INQUIRY,
              `policy_found=true policyNumber=${result.policyNumber} claimRequestNum=${result.claimRequestNum}`,
            ),
          },
          true,
        );
      } else {
        send(
          {
            text: systemUiMessage(
              USER_SUBMITTED_POLICY_INQUIRY,
              "policy_found=false",
            ),
          },
          false,
        );
      }
    } catch (error: any) {
      setLoading(false);
      console.error("❌ [Altair] Policy inquiry error:", error);
      send(
        {
          text: systemUiMessage(
            USER_SUBMITTED_POLICY_INQUIRY,
            `error=${error.message}`,
          ),
        },
        false,
      );
    }
  }, [send, policyNumberInput, setFlowStep, setLoading]);

  const handleSurveyOptionClick = useCallback(
    (option: "live" | "upload") => {
      if (!send) return;

      console.log(`📋 [Altair] User selected survey option: ${option}`);
      setFlowStep("idle");

      if (option === "live") {
        send({ text: systemUiMessage(USER_SELECTED_LIVE_SURVEY) }, true);
      } else {
        send({ text: systemUiMessage(USER_SELECTED_UPLOAD_IMAGES) }, true);
      }
    },
    [send, setFlowStep],
  );

  const handlePolicySurveyOptionClick = useCallback(
    (option: "live" | "upload") => {
      if (!send) return;

      console.log(
        `📋 [Altair] User selected survey option (policy flow): ${option}`,
      );
      setFlowStep("idle");

      if (option === "live") {
        send({ text: systemUiMessage(USER_SELECTED_LIVE_SURVEY) }, true);
      } else {
        send({ text: systemUiMessage(USER_SELECTED_UPLOAD_IMAGES) }, true);
      }
    },
    [send, setFlowStep],
  );

  const handlePolicyVehicleImagesUploaded = useCallback(
    async (files: File[]) => {
      if (!client) return;

      console.log(
        `📋 [Altair] User uploaded ${files.length} policy vehicle images`,
      );

      const sides: VehicleSide[] = [
        "Chassis",
        "Front",
        "Right",
        "Back",
        "Left",
      ];
      const newImages: VehicleSurveyImage[] = [];

      for (let i = 0; i < Math.min(files.length, 5); i++) {
        const file = files[i];
        const reader = new FileReader();
        const imageData = await new Promise<string>((resolve) => {
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(file);
        });

        newImages.push({
          side: sides[i],
          data: imageData,
          capturedAt: new Date().toISOString(),
        });
      }

      setSurveyImages(newImages);

      if (newImages.length >= 5) {
        setFlowStep("idle");

        // Images are stored in state, analysis will be done via instruction message
        console.log(
          `📋 [Altair] ${newImages.length} policy vehicle images ready for analysis`,
        );
        // Send system message that images were uploaded
        send({ text: systemUiMessage(USER_UPLOADED_VEHICLE_IMAGES) }, true);
      }
    },
    [client, send, setFlowStep],
  );

  const handleVehicleUploadImagesUploaded = useCallback(
    async (files: File[]) => {
      if (!send) return;
      console.log(
        `📋 [Altair] User uploaded ${files.length} vehicle images (vehicle inspection)`,
      );
      const sides: VehicleSide[] = [
        "Chassis",
        "Front",
        "Right",
        "Back",
        "Left",
      ];
      const newImages: VehicleSurveyImage[] = [];
      for (let i = 0; i < Math.min(files.length, 5); i++) {
        const file = files[i];
        const reader = new FileReader();
        const imageData = await new Promise<string>((resolve) => {
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(file);
        });
        newImages.push({
          side: sides[i],
          data: imageData,
          capturedAt: new Date().toISOString(),
        });
      }
      setSurveyImages(newImages);
      if (newImages.length >= 5) {
        setFlowStep("idle");
        send({ text: systemUiMessage(USER_UPLOADED_VEHICLE_IMAGES) }, true);
      }
    },
    [send, setFlowStep],
  );

  const handleClaimVehicleImagesUploaded = useCallback(
    async (files: File[]) => {
      if (!send) return;

      console.log(
        `📋 [Altair] User uploaded ${files.length} claim vehicle images`,
      );

      const sides: VehicleSide[] = [
        "Chassis",
        "Front",
        "Right",
        "Back",
        "Left",
      ];
      const newImages: VehicleSurveyImage[] = [];

      for (let i = 0; i < Math.min(files.length, 5); i++) {
        const file = files[i];
        const reader = new FileReader();
        const imageData = await new Promise<string>((resolve) => {
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(file);
        });

        newImages.push({
          side: sides[i],
          data: imageData,
          capturedAt: new Date().toISOString(),
        });
      }

      console.log("📸 [Altair] Setting claim survey images:", {
        count: newImages.length,
        sides: newImages.map((img) => img.side),
        hasData: newImages.every((img) => !!img.data && img.data.length > 0),
      });

      setClaimSurveyImages(newImages);

      // Also save to session storage for persistence
      try {
        const existingData = sessionStorage.getItem("saaed_user_data");
        const userData = existingData ? JSON.parse(existingData) : {};
        userData.claimSurveyImages = newImages;
        sessionStorage.setItem("saaed_user_data", JSON.stringify(userData));
        console.log("✅ [Altair] Claim survey images saved to session storage");
      } catch (error) {
        console.error(
          "❌ [Altair] Failed to save claim survey images to session storage:",
          error,
        );
      }

      if (newImages.length >= 5) {
        setFlowStep("idle");
        // Send system message that images were uploaded
        send({ text: systemUiMessage(USER_UPLOADED_VEHICLE_IMAGES) }, true);
      }
    },
    [send, setFlowStep],
  );

  const handleWorkshopOptionClick = useCallback(
    (option: "contracted" | "non_contracted") => {
      if (!send) return;

      console.log(`📋 [Altair] User selected workshop option: ${option}`);
      setFlowStep("idle");

      if (option === "contracted") {
        send(
          { text: systemUiMessage(USER_SELECTED_CONTRACTED_WORKSHOP) },
          true,
        );
      } else {
        send(
          { text: systemUiMessage(USER_SELECTED_NON_CONTRACTED_WORKSHOP) },
          true,
        );
      }
    },
    [send, setFlowStep],
  );

  const handleContractedWorkshopSelect = useCallback(
    async (workshop: ContractedWorkshop) => {
      if (!send) return;

      console.log(`📋 [Altair] User selected contracted workshop:`, workshop);
      setFlowStep("idle");

      try {
        const claimData = JSON.parse(
          sessionStorage.getItem("saaed_claim_data") || "{}",
        );
        await chooseContractedWorkshop(
          workshop.workshopId,
          claimData.policyNumber || "",
          claimData.claimRequestNum || "",
        );

        send(
          {
            text: systemUiMessage(
              USER_CHOSE_CONTRACTED_WORKSHOP,
              `workshopId=${workshop.workshopId} workshopName=${workshop.workshopName}`,
            ),
          },
          true,
        );
      } catch (error: any) {
        console.error("❌ [Altair] Error selecting workshop:", error);
        send(
          {
            text: systemUiMessage(
              USER_CHOSE_CONTRACTED_WORKSHOP,
              `error=${error.message}`,
            ),
          },
          false,
        );
      }
    },
    [send, setFlowStep],
  );

  const handleNonContractedFormSubmit = useCallback(async () => {
    if (!send) return;

    if (!nonContractedForm.workshopName.trim()) {
      alert("برجاء إدخال اسم مركز الخدمة");
      return;
    }

    console.log(
      `📋 [Altair] Submitting non-contracted workshop:`,
      nonContractedForm,
    );

    try {
      const claimData = JSON.parse(
        sessionStorage.getItem("saaed_claim_data") || "{}",
      );
      await chooseNonContractedWorkshop(
        nonContractedForm,
        claimData.policyNumber || "",
        claimData.claimRequestNum || "",
      );

      setFlowStep("idle");
      setNonContractedForm({
        city: "",
        area: "",
        workshopAddress: "",
        workshopName: "",
        contactPersonName: "",
        mobile: "",
        phoneNumber: "",
        hotline: "",
      });

      send(
        {
          text: systemUiMessage(
            USER_SUBMITTED_NON_CONTRACTED_WORKSHOP,
            `workshopName=${nonContractedForm.workshopName}`,
          ),
        },
        true,
      );
    } catch (error: any) {
      console.error("❌ [Altair] Error submitting workshop:", error);
      send(
        {
          text: systemUiMessage(
            USER_SUBMITTED_NON_CONTRACTED_WORKSHOP,
            `error=${error.message}`,
          ),
        },
        false,
      );
    }
  }, [send, nonContractedForm, setFlowStep]);

  const handleClaimNumberAcknowledge = useCallback(() => {
    if (!send) return;

    console.log(`📋 [Altair] User acknowledged claim number`);

    setFlowStep("idle");

    send({ text: systemUiMessage(USER_ACKNOWLEDGED_CLAIM_NUMBER) }, false);

    // This is the end of claim flow, no next instruction needed
    console.log("✅ [Altair] Claim flow completed");
  }, [send, setFlowStep]);

  // ─────────────────────────────────────────────
  // 7) RENDER UI
  // ─────────────────────────────────────────────
  return (
    <div className="padding-adjust" >
   

      {/* TEST MODE - Optional debug panel */}
      {TEST_MODE && (
        <div
          style={{
            position: "fixed",
            top: "10px",
            right: "10px",
            zIndex: 9999,
            backgroundColor: "#ff9800",
            padding: "15px",
            borderRadius: "8px",
            boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
          }}
        >
          <h4 style={{ margin: "0 0 10px 0", color: "white" }}>🧪 TEST MODE</h4>
          <div style={{ color: "white", fontSize: "12px" }}>
            <div>flowStep: {flowStep}</div>
            <div>companies: {companies.length}</div>
            <div>selectedPackageCode: {selectedPackageCode || "-"}</div>
          </div>
        </div>
      )}

      {/* Altair Chart Display (if used) */}
      <div className="vega-embed" ref={embedRef} />

      {/* Package selection UI */}
      <PackageSelection
        visible={flowStep === "package_selection"}
        companies={companies}
        selectedPackageCode={selectedPackageCode}
        onSelectPackage={handleSelectPackage}
        onConfirm={handlePackageConfirm}
      />

      {/* Unified Document Upload Camera — stays open across entire OCR phase */}
      <FullScreenCameraCapture
        visible={documentPhaseActive}
        title={lastDocTitleRef.current}
        onImagesUploaded={handleImagesUploaded}
        onClose={() => {
          setDocumentPhaseActive(false);
          setFlowStep?.("idle");
        }}
      />

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* UNDERWRITING QUESTIONS UI */}
      {/* ═══════════════════════════════════════════════════════════ */}
      {(() => {
        const q = getUnderwritingQuestionByFlowStep(
          "underwriting_vehicle_usage",
        );
        const options = q?.options?.length
          ? q.options.map((o) => ({
            value: coerceUnderwritingValue(o.value),
            labelAr: o.labelAr,
          }))
          : [
            { value: 1, labelAr: "شخصي" },
            { value: 2, labelAr: "أوبر-كريم" },
            { value: 3, labelAr: "شركات" },
          ];
        return (
          <UnderwritingChoicePopup
            visible={flowStep === "underwriting_vehicle_usage"}
            title={q?.titleAr || "ما نوع استخدام السيارة؟"}
            options={options}
            note={q?.noteAr || undefined}
            onSelect={(v) =>
              underwritingHandlers.onVehicleUsageSelect(v as number)
            }
          />
        );
      })()}
      {(() => {
        const q = getUnderwritingQuestionByFlowStep(
          "underwriting_had_accident",
        );
        const options = q?.options?.length
          ? q.options.map((o) => ({
            value: coerceUnderwritingValue(o.value),
            labelAr: o.labelAr,
          }))
          : [
            { value: true, labelAr: "نعم" },
            { value: false, labelAr: "لا" },
          ];
        return (
          <UnderwritingChoicePopup
            visible={flowStep === "underwriting_had_accident"}
            title={q?.titleAr || "هل سبق للسيارة التعرض للحوادث آخر سنة؟"}
            options={options}
            note={q?.noteAr || undefined}
            onSelect={(v) =>
              underwritingHandlers.onHadAccidentSelect(v as boolean)
            }
          />
        );
      })()}
      {(() => {
        const q = getUnderwritingQuestionByFlowStep(
          "underwriting_night_parking",
        );
        const options = q?.options?.length
          ? q.options.map((o) => ({
            value: coerceUnderwritingValue(o.value),
            labelAr: o.labelAr,
          }))
          : [
            { value: 1, labelAr: "جراج خاص" },
            { value: 2, labelAr: "جراج العمارة" },
            { value: 3, labelAr: "في الشارع" },
            { value: 4, labelAr: "داخل كمبوند" },
          ];
        return (
          <UnderwritingChoicePopup
            visible={flowStep === "underwriting_night_parking"}
            title={q?.titleAr || "أين تركن السيارة ليلاً؟"}
            options={options}
            note={q?.noteAr || undefined}
            onSelect={(v) =>
              underwritingHandlers.onNightParkingSelect(v as number)
            }
          />
        );
      })()}
      {(() => {
        const q = getUnderwritingQuestionByFlowStep("underwriting_violations");
        return (
          <UnderwritingNumberInputPopup
            visible={flowStep === "underwriting_violations"}
            title={q?.titleAr || "ما إجمالي المخالفات آخر سنة تقريباً؟"}
            placeholder={q?.placeholderAr || "أدخل عدد المخالفات"}
            onSubmit={underwritingHandlers.onViolationsSubmit}
          />
        );
      })()}
      {(() => {
        const q = getUnderwritingQuestionByFlowStep(
          "underwriting_tracking_device",
        );
        const options = q?.options?.length
          ? q.options.map((o) => ({
            value: coerceUnderwritingValue(o.value),
            labelAr: o.labelAr,
          }))
          : [
            { value: true, labelAr: "نعم" },
            { value: false, labelAr: "لا" },
          ];
        return (
          <UnderwritingChoicePopup
            visible={flowStep === "underwriting_tracking_device"}
            title={q?.titleAr || "هل توافق على تركيب جهاز تتبع؟"}
            note={
              q?.noteAr || "تحصل على خصم 5% عند التجديد في حالة تركيب الجهاز"
            }
            options={options}
            onSelect={(v) =>
              underwritingHandlers.onTrackingDeviceSelect(v as boolean)
            }
          />
        );
      })()}
      {(() => {
        const q = getUnderwritingQuestionByFlowStep(
          "underwriting_vehicle_financing",
        );
        const options = q?.options?.length
          ? q.options.map((o) => ({
            value: coerceUnderwritingValue(o.value),
            labelAr: o.labelAr,
          }))
          : [
            { value: 1, labelAr: "نعم" },
            { value: 4, labelAr: "لا" },
          ];
        return (
          <UnderwritingChoicePopup
            visible={flowStep === "underwriting_vehicle_financing"}
            title={q?.titleAr || "هل السيارة عليها تمويل أو حظر بيع؟"}
            note={q?.noteAr || "تمويل بنكي أو شركة تقسيط أو حظر بيع"}
            options={options}
            onSelect={(v) =>
              underwritingHandlers.onVehicleFinancingSelect(v as number)
            }
          />
        );
      })()}
      {(() => {
        const q = getUnderwritingQuestionByFlowStep(
          "underwriting_number_of_drivers",
        );
        return (
          <UnderwritingNumberInputPopup
            visible={flowStep === "underwriting_number_of_drivers"}
            title={q?.titleAr || "كم عدد السائقين المستخدمين للسيارة؟"}
            placeholder={q?.placeholderAr || "أدخل عدد السائقين"}
            onSubmit={underwritingHandlers.onNumberOfDriversSubmit}
          />
        );
      })()}

      {/* Vehicle Survey Status - Car Image with Checkboxes + Capture button */}
      <VehicleSurveyOverlay
        visible={
          flowStep === "vehicle_survey" || flowStep === "claim_vehicle_survey"
        }
        flowStep={flowStep}
        surveyImages={surveyImages}
        claimSurveyImages={claimSurveyImages}
        nextSideToCapture={nextSideToCapture}
        onCaptureClick={
          onRequestCaptureFrame ? handleSurveyCaptureClick : undefined
        }
        captureInProgress={surveyCaptureInProgress}
        isValidating={isValidating}
        validationError={validationError}
      />

      <SurveyOptions
        visible={
          flowStep === "policy_survey_options" ||
          flowStep === "vehicle_survey_options"
        }
        title="اختر طريقة المعاينة"
        primaryLabel="معاينة لايف"
        primaryDescription="فتح الكاميرا والتصوير مباشرة"
        secondaryLabel="رفع 5 صور"
        secondaryDescription="رفع 5 صور للسيارة (شاسيه + 4 جهات)"
        onLive={() => handlePolicySurveyOptionClick("live")}
        onUpload={() => handlePolicySurveyOptionClick("upload")}
      />

      {/* Vehicle Images Upload (5 images) - Policy or Vehicle Inspection */}
      <PolicyVehicleUpload
        visible={
          flowStep === "policy_vehicle_upload" || flowStep === "vehicle_upload"
        }
        onImagesUploaded={
          flowStep === "vehicle_upload"
            ? handleVehicleUploadImagesUploaded
            : handlePolicyVehicleImagesUploaded
        }
        uploadedCount={surveyImages.length}
      />

      {/* Policy confirmation screen */}
      <PolicyConfirmation
        visible={flowStep === "policy_confirmation"}
        selectedPackage={selectedPackage}
        onConfirm={handleConfirmPolicyClick}
      />

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* CLAIM REQUEST UI COMPONENTS */}
      {/* ═══════════════════════════════════════════════════════════ */}

      {/* Claim: Policy Inquiry Popup */}
      <ClaimPolicyInquiry
        visible={flowStep === "claim_policy_inquiry"}
        policyNumber={policyNumberInput}
        onPolicyNumberChange={setPolicyNumberInput}
        onSubmit={handlePolicyInquirySubmit}
      />

      {/* Claim: Survey Options (Live / Upload) */}
      <SurveyOptions
        visible={flowStep === "claim_survey_options"}
        title="اختر طريقة المعاينة"
        primaryLabel="معاينة لايف"
        primaryDescription="فتح الكاميرا والتصوير مباشرة"
        secondaryLabel="رفع صور"
        secondaryDescription="رفع 5 صور للسيارة (شاسيه + 4 جهات)"
        onLive={() => handleSurveyOptionClick("live")}
        onUpload={() => handleSurveyOptionClick("upload")}
      />

      {/* Claim: Vehicle Images Upload (5 images) */}
      <ClaimVehicleUpload
        visible={flowStep === "claim_vehicle_upload"}
        onImagesUploaded={handleClaimVehicleImagesUploaded}
        uploadedCount={claimSurveyImages.length}
      />

      {/* Claim: Workshop Options (Contracted / Non-Contracted) */}
      <ClaimWorkshopOptions
        visible={flowStep === "claim_workshop_options"}
        onSelectContracted={() => handleWorkshopOptionClick("contracted")}
        onSelectNonContracted={() =>
          handleWorkshopOptionClick("non_contracted")
        }
      />

      {/* Claim: Contracted Workshops List */}
      <ContractedWorkshopsList
        visible={flowStep === "claim_contracted_workshops"}
        workshops={contractedWorkshops}
        onSelect={handleContractedWorkshopSelect}
      />

      {/* Claim: Non-Contracted Workshop Form */}
      <NonContractedWorkshopForm
        visible={flowStep === "claim_non_contracted_form"}
        form={nonContractedForm}
        onChange={setNonContractedForm}
        onSubmit={handleNonContractedFormSubmit}
      />

      {/* Claim: Claim Number Display */}
      <ClaimNumberDisplay
        visible={flowStep === "claim_number_display"}
        claimNumber={(() => {
          try {
            const claimData = JSON.parse(
              sessionStorage.getItem("saaed_claim_data") || "{}",
            );
            return claimData.claimRequestNum || "";
          } catch {
            return "";
          }
        })()}
        onAcknowledge={handleClaimNumberAcknowledge}
      />

      {/* (اختياري) Debug: عرض حالة بيانات البطاقة لو حابة تشوفيها */}
      {/* 
      <pre style={{ fontSize: 10 }}>
        {JSON.stringify(customerNationalIdData, null, 2)}
      </pre>
      */}

      {/* Extracted Data Confirmation Modal */}
      <ExtractedDataConfirmationModal
        isOpen={extractedDataModal.isOpen}
        title={extractedDataModal.title}
        data={extractedDataModal.data}
        documentType={extractedDataModal.documentType}
        previewImageSrc={extractedDataModal.previewImageSrc}
        onConfirm={extractedDataModal.onConfirm}
        onCancel={() =>
          setExtractedDataModal((prev) => ({ ...prev, isOpen: false }))
        }
      />

      {/* Policy Number Modal */}
      <PolicyNumberModal
        isOpen={policyNumberModal.isOpen}
        policyNumber={policyNumberModal.policyNumber}
        title={policyNumberModal.title}
        message={policyNumberModal.message}
        onConfirm={policyNumberModal.onConfirm}
      />
    </div>
  );
}

export const Altair = memo(AltairComponent);
